import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { Anchor } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AuthCallback — Emergent Google OAuth landing.
 *
 * Detects #session_id= in URL hash, POSTs to /auth/google/session, stores JWT,
 * then hard-navigates to /dashboard. On backend refusal, displays a French
 * error screen with a "Retour à la connexion" button (data-testid=google-auth-error).
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH.
 */
const AuthCallback = () => {
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      setError("Session Google invalide (session_id manquant).");
      return;
    }
    const session_id = decodeURIComponent(match[1]);

    // Clean the hash from the URL before doing anything else
    try { window.history.replaceState(null, '', window.location.pathname); } catch (e) { /* noop */ }

    (async () => {
      try {
        const resp = await axios.post(`${API}/auth/google/session`, { session_id }, { timeout: 30000 });
        const { token, user } = resp.data || {};
        if (!token) throw new Error('Réponse serveur invalide');
        localStorage.setItem('odys_token', token);
        // Hard navigate to force AuthProvider to re-init with the new token
        window.location.replace('/dashboard');
      } catch (e) {
        const detail = e.response?.data?.detail || e.message || 'Erreur inconnue';
        setError(detail);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-6"
        style={{ background: 'var(--paper, #FAF7F1)', color: 'var(--ink, #0F2540)' }}
        data-testid="google-auth-error"
      >
        <div className="max-w-md w-full dossier-card !p-8 text-center">
          <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ background: 'rgba(201,168,106,0.15)' }}>
            <Anchor className="w-7 h-7" style={{ color: 'var(--gold, #C9A86A)' }} />
          </div>
          <h1 className="font-heading text-2xl font-bold mb-3">Connexion Google impossible</h1>
          <p className="text-sm text-muted-foreground mb-6 whitespace-pre-line">{error}</p>
          <button
            onClick={() => { window.location.replace('/login'); }}
            className="w-full px-5 py-2.5 rounded-lg font-semibold transition-transform hover:-translate-y-0.5"
            style={{ background: 'var(--gold, #C9A86A)', color: 'var(--paper, #FAF7F1)' }}
            data-testid="google-auth-error-back-btn"
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--paper, #FAF7F1)', color: 'var(--ink, #0F2540)' }}>
      <div className="text-center">
        <div className="mx-auto w-12 h-12 rounded-full border-2 border-transparent animate-spin mb-4" style={{ borderTopColor: 'var(--gold, #C9A86A)', borderRightColor: 'var(--gold, #C9A86A)' }} />
        <p className="text-sm text-muted-foreground">Connexion à L'Équipage…</p>
      </div>
    </div>
  );
};

export default AuthCallback;
